import React, { useCallback, useMemo, useState } from "react";
import {
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QFooter,
  QFormControl,
  QHeader,
  QHorizontalMetadata,
  QInput,
  QSelect,
  QStack,
  QText,
  QTextarea,
  QTitle,
  useToastProvider,
} from "@qualio/ui-components";
import {
  Controller,
  FormProvider,
  useForm,
  DefaultValues,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formToRisk, ISORiskForm } from "../validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { ResourceComponentField } from "../../../components/ResourceComponentField";
import {
  isRequirementPolicy,
  isRiskPolicy,
  isTestCasePolicy,
} from "@design-controls/types";
import { useCurrentConfigs } from "../../../hooks/useCurrentConfigs";
import { RelateDesignElements } from "../../../components/RelateDesignElements/RelateDesignElements";
import { FormRiskScore } from "./FormRiskScore";
import { riskScore } from "../../../domain/isoRequiresControl";
import { useUpdateDesignElement } from "../../../hooks/designElement/useUpdateDesignElement";
import { useNavigate } from "react-router";
import { handleFormError } from "../../../api/handleFormError";

type FormProps = {
  risk: ISORiskForm;
  onClose: () => void;
};

const containsRiskControl = (risk: ISORiskForm) =>
  !!(
    risk.mitigatesRequirements?.length ||
    risk.mitigatesTestCases?.length ||
    risk.riskControl?.mitigation
  );

export const Form: React.FC<FormProps> = ({ risk, onClose }) => {
  const { t } = useTranslation();
  const configs = useCurrentConfigs();
  const riskConfig = configs.find(isRiskPolicy);
  const [manualRiskControlEnabled, setManualRiskControlEnabled] =
    useState<boolean>(containsRiskControl(risk));

  const { isLoading, mutate } = useUpdateDesignElement();
  const navigate = useNavigate();
  const { showToast } = useToastProvider();

  const defaultValues = risk as DefaultValues<ISORiskForm>;
  const methods = useForm<ISORiskForm>({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(ISORiskForm),
  });

  const { categoryResourceIdentifier } = useMemo(() => {
    return {
      categoryResourceIdentifier: riskConfig?.category?.[0].resourceIdentifier,
    };
  }, [riskConfig]);

  const requirementTypes = useMemo(() => {
    return configs.filter(isRequirementPolicy).map((item) => item.type);
  }, [configs]);

  const testCaseTypes = useMemo(() => {
    return configs.filter(isTestCasePolicy).map((item) => item.type) as (
      | "testCase1"
      | "testCase2"
      | "testCase3"
    )[];
  }, [configs]);

  const {
    watch,
    setError,
    formState: { errors },
  } = methods;

  const [preMitigationProbability, preMitigationSeverity] = watch([
    "preMitigation.probability",
    "preMitigation.severity",
  ]) as string[];

  const [postMitigationProbability, postMitigationSeverity] = watch([
    "postMitigation.probability",
    "postMitigation.severity",
  ]) as string[];

  const riskControlRequired = useMemo(() => {
    if (!riskConfig) {
      return false;
    }

    const score =
      riskScore(riskConfig, preMitigationProbability, preMitigationSeverity) ??
      "";
    return !riskConfig?.assessment.iso.acceptableLevels.includes(score);
  }, [preMitigationProbability, preMitigationSeverity, riskConfig]);

  const onSubmit = useCallback(
    (formData: ISORiskForm) => {
      if (!riskConfig) {
        return;
      }

      const data = formToRisk(formData, riskConfig);
      mutate(data as any, {
        onSuccess: () => navigate(`../risk/iso/${data.id}`),
        onError: (error) => {
          showToast({
            title: t("risks.iso.edit.notifications.saveError.title"),
            description: t(
              "risks.iso.edit.notifications.saveError.description",
            ),
            status: "error",
          });
          handleFormError(error, setError);
        },
      });
    },
    [riskConfig, mutate, navigate, setError],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} data-cy="edit-iso-risk">
        <QBodyLayout.Default size="md">
          <QHeader>
            <QTitle>
              <QText as="span" color="gray.500">
                {risk.code}
              </QText>{" "}
              {risk.title}
            </QTitle>
            <QHorizontalMetadata>
              <QDateMetadataItem
                label={t("common.metadata.created")}
                value={new Date(risk.created)}
                withTime={true}
              />
              <QDateMetadataItem
                label={t("common.metadata.lastModified")}
                value={new Date(risk.updated)}
                withTime={true}
              />
            </QHorizontalMetadata>
          </QHeader>
          <QStack gap="16px">
            <QText fontStyle="italic">
              {t("risks.iso.edit.helpers.requiredFields")}
            </QText>
            <QFormControl
              label={t("risks.iso.edit.fields.title")}
              isRequired
              isInvalid={!!errors?.title}
              error={errors?.title?.message}
            >
              <Controller
                name="title"
                render={({ field: { value, onChange } }) => (
                  <QInput value={value} onChange={onChange} data-cy="title" />
                )}
              />
            </QFormControl>
            {categoryResourceIdentifier && (
              <ResourceComponentField
                name="category"
                label={t("risks.iso.edit.fields.category")}
                resourceSubType={categoryResourceIdentifier}
              />
            )}
            <QText fontSize="xl" fontWeight={600}>
              {t("risks.iso.edit.sections.initialAssessment")}
            </QText>

            <QFormControl
              label={t("risks.iso.edit.fields.hazard")}
              isInvalid={!!errors?.hazard}
              error={errors?.hazard?.message}
            >
              <Controller
                name="hazard"
                render={({ field: { value, onChange } }) => (
                  <QInput value={value} onChange={onChange} data-cy="hazard" />
                )}
              />
            </QFormControl>

            <QStack direction="row" columnGap="24px">
              <QBox width="188px">
                <QFormControl
                  label={t("risks.iso.edit.fields.probability")}
                  isInvalid={!!errors?.preMitigation?.probability}
                  data-cy="premitigation-probability"
                >
                  <Controller
                    name="preMitigation.probability"
                    render={({ field: { value, onChange } }) => (
                      <QSelect
                        value={value}
                        options={(
                          riskConfig?.assessment.iso.probability ?? []
                        ).map((item) => ({ value: item, label: item }))}
                        onChange={(item) => item && onChange(item.value)}
                      />
                    )}
                  />
                </QFormControl>
              </QBox>
              <QBox width="188px">
                <QFormControl
                  label={t("risks.iso.edit.fields.severity")}
                  isInvalid={!!errors?.preMitigation?.severity}
                  data-cy="premitigation-severity"
                >
                  <Controller
                    name="preMitigation.severity"
                    render={({ field: { value, onChange } }) => (
                      <QSelect
                        value={value}
                        options={(
                          riskConfig?.assessment.iso.severity ?? []
                        ).map((item) => ({ value: item, label: item }))}
                        onChange={(item) => item && onChange(item.value)}
                      />
                    )}
                  />
                </QFormControl>
              </QBox>
              <QStack gap="4px" width="103px">
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.edit.labels.risk")}
                </QText>
                <QBox>
                  <FormRiskScore
                    probability={preMitigationProbability}
                    severity={preMitigationSeverity}
                  />
                </QBox>
              </QStack>
              <QStack gap="4px" width="103px">
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.edit.labels.riskControl")}
                </QText>
                {riskControlRequired ? (
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.iso.edit.labels.riskControlRequired")}
                  </QText>
                ) : (
                  <QText fontSize="sm">
                    {t("risks.iso.edit.labels.riskControlNotRequired")}
                  </QText>
                )}
              </QStack>
            </QStack>

            <QFormControl
              label={t("risks.iso.edit.fields.reasonableForeseeableUseMisuse")}
              isInvalid={!!errors?.foreseeableUseMisuse}
              error={errors?.foreseeableUseMisuse?.message}
            >
              <Controller
                name="foreseeableUseMisuse"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="foreseeableUseMisuse"
                  />
                )}
              />
            </QFormControl>

            <QFormControl
              label={t("risks.iso.edit.fields.hazardousSituation")}
              isInvalid={!!errors?.hazardSituation}
              error={errors?.hazardSituation?.message}
            >
              <Controller
                name="hazardSituation"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="hazardSituation"
                  />
                )}
              />
            </QFormControl>

            <QFormControl
              label={t("risks.iso.edit.fields.harm")}
              isInvalid={!!errors?.harm}
              error={errors?.harm?.message}
            >
              <Controller
                name="harm"
                render={({ field: { value, onChange } }) => (
                  <QTextarea value={value} onChange={onChange} data-cy="harm" />
                )}
              />
            </QFormControl>

            {requirementTypes.length > 0 && (
              <QFormControl>
                <Controller
                  name="sourceRequirements"
                  render={({ field: { value, onChange } }) => (
                    <RelateDesignElements
                      label={t(
                        "risks.iso.edit.relatedElements.sourceRequirements",
                      )}
                      resource="dc-requirement"
                      resourceSubType={requirementTypes}
                      onChange={onChange}
                      value={value}
                      data-cy="add-source-requirement"
                      buttonLabel={t(
                        "risks.iso.edit.relatedElements.addRequirements",
                      )}
                    />
                  )}
                />
              </QFormControl>
            )}

            {testCaseTypes.length > 0 && (
              <QFormControl>
                <Controller
                  name="sourceTestCases"
                  render={({ field: { value, onChange } }) => (
                    <RelateDesignElements
                      label={t(
                        "risks.iso.edit.relatedElements.sourceTestCases",
                      )}
                      resource="dc-testCase"
                      resourceSubType={testCaseTypes}
                      onChange={onChange}
                      value={value}
                      data-cy="add-source-testcase"
                      buttonLabel={t(
                        "risks.iso.edit.relatedElements.addTestCases",
                      )}
                    />
                  )}
                />
              </QFormControl>
            )}

            <QStack>
              <QText fontSize="xl" fontWeight={600}>
                {t("risks.iso.edit.sections.riskControl")}
              </QText>
              <QText>{t("risks.iso.edit.helpers.riskControlInfo")}</QText>
            </QStack>

            {!manualRiskControlEnabled && !riskControlRequired && (
              <QButton
                variant="outline"
                onClick={() => setManualRiskControlEnabled(true)}
                data-cy="add-risk-control-btn"
              >
                {t("risks.iso.edit.actions.addRiskControl")}
              </QButton>
            )}

            {(manualRiskControlEnabled || riskControlRequired) && (
              <>
                <QFormControl
                  label={t("risks.iso.edit.fields.riskControlDescription")}
                  isInvalid={!!errors?.riskControl?.mitigation}
                  error={errors?.riskControl?.mitigation?.message}
                >
                  <Controller
                    name="riskControl.mitigation"
                    render={({ field: { value, onChange } }) => (
                      <QTextarea
                        value={value}
                        onChange={onChange}
                        data-cy="riskControl.mitigation"
                      />
                    )}
                  />
                </QFormControl>

                {requirementTypes.length > 0 && (
                  <QFormControl>
                    <Controller
                      name="mitigatesRequirements"
                      render={({ field: { value, onChange } }) => (
                        <RelateDesignElements
                          label={t(
                            "risks.iso.edit.relatedElements.riskControlRequirements",
                          )}
                          resource="dc-requirement"
                          resourceSubType={requirementTypes}
                          onChange={onChange}
                          value={value}
                          data-cy="add-mitigation-requirement"
                          buttonLabel={t(
                            "risks.iso.edit.relatedElements.addRequirements",
                          )}
                        />
                      )}
                    />
                  </QFormControl>
                )}

                {testCaseTypes.length > 0 && (
                  <QFormControl>
                    <Controller
                      name="mitigatesTestCases"
                      render={({ field: { value, onChange } }) => (
                        <RelateDesignElements
                          label={t(
                            "risks.iso.edit.relatedElements.riskControlTestCases",
                          )}
                          resource="dc-testCase"
                          resourceSubType={testCaseTypes}
                          onChange={onChange}
                          value={value}
                          data-cy="add-mitigation-testcase"
                          buttonLabel={t(
                            "risks.iso.edit.relatedElements.addTestCases",
                          )}
                        />
                      )}
                    />
                  </QFormControl>
                )}

                <QText fontSize="xl" fontWeight={600}>
                  {t("risks.iso.edit.sections.finalAssessment")}
                </QText>

                <QStack direction="row" columnGap="24px">
                  <QBox width="188px">
                    <QFormControl
                      label={t("risks.iso.edit.fields.probability")}
                      isInvalid={!!errors?.postMitigation?.probability}
                      data-cy="postmitigation-probability"
                    >
                      <Controller
                        name="postMitigation.probability"
                        render={({ field: { value, onChange } }) => (
                          <QSelect
                            value={value}
                            options={(
                              riskConfig?.assessment.iso.probability ?? []
                            ).map((item) => ({ value: item, label: item }))}
                            onChange={(item) => item && onChange(item.value)}
                          />
                        )}
                      />
                    </QFormControl>
                  </QBox>
                  <QBox width="188px">
                    <QFormControl
                      label={t("risks.iso.edit.fields.severity")}
                      isInvalid={!!errors?.postMitigation?.severity}
                      data-cy="postmitigation-severity"
                    >
                      <Controller
                        name="postMitigation.severity"
                        render={({ field: { value, onChange } }) => (
                          <QSelect
                            value={value}
                            options={(
                              riskConfig?.assessment.iso.severity ?? []
                            ).map((item) => ({ value: item, label: item }))}
                            onChange={(item) => item && onChange(item.value)}
                          />
                        )}
                      />
                    </QFormControl>
                  </QBox>
                  <QStack gap="4px" width="103px">
                    <QText fontSize="sm" fontWeight={600}>
                      {t("risks.iso.edit.labels.risk")}
                    </QText>
                    <QBox>
                      <FormRiskScore
                        probability={postMitigationProbability}
                        severity={postMitigationSeverity}
                      />
                    </QBox>
                  </QStack>
                </QStack>
              </>
            )}
          </QStack>

          <QFooter>
            <QButtonGroup>
              <QButton
                variant="outline"
                onClick={onClose}
                data-cy="cancel"
                isDisabled={isLoading}
              >
                {t("risks.iso.edit.actions.cancel")}
              </QButton>
              <QButton
                type="submit"
                data-cy="save-changes"
                isLoading={isLoading}
              >
                {t("risks.iso.edit.actions.saveChanges")}
              </QButton>
            </QButtonGroup>
          </QFooter>
        </QBodyLayout.Default>
      </form>
    </FormProvider>
  );
};
